import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { css } from 'styled-components'
import dsTheme from '../constants/design-system/theme.json'
import { theme } from '../constants/theme'

import withApp from '../components/withApp/withApp'
import SEO from '../components/Seo'
import TrustPilot from '../components/TrustPilot'
import PlanSection from '../components/PlanSection'
import TextLink from '../components/TextLink'
import Button from '../components/Button'

// Modular system components
import Hero from '../modular-system/Hero'
import FaqSection from '../modular-system/FaqSection'
import FiftyFifty from '../modular-system/FiftyFifty'
import LogoGarden from '../modular-system/LogoGarden'

// Global Styled Components
import BlockContainer from '../components/styles/BlockContainer'
import CenteredButton from '../components/styles/CenteredButton'
import FullWidthContainer from '../components/styles/FullWidthContainer'
import SiteContainer from '../components/styles/SiteContainer'
import Spacer from '../components/styles/Spacer'

//  ßsvg Images
import HeroImage from '../static/images/seo-landing-pages/no-credit-check-people.svg'
import DataImage from '../static/images/seo-landing-pages/no-credit-check-data.svg'
import UnlimitedImage from '../static/images/seo-landing-pages/no-credit-check-unlimited.svg'
import PayAsYouGo from '../static/images/seo-landing-pages/pay-as-you-go.svg'
import DataSimDevice from '../static/images/seo-landing-pages/data-sim-device.svg'
import { RICH_DATA_STRINGS } from '../constants/globalStrings'

const { colors } = dsTheme

const SimOnly = ({ plans: { plans } }) => (
  <Fragment>
    <SEO
      itemType={RICH_DATA_STRINGS.FAQ_ITEM_TYPE}
      title={simOnlyContent.seo.title}
      description={simOnlyContent.seo.description}
      keywords={simOnlyContent.seo.keywords}
    />

    <FullWidthContainer background={colors['UI/DarkGrey']}>
      <SiteContainer>
        <Hero
          heading={simOnlyContent.heading}
          subHeading={simOnlyContent.subHeading}
          benefits={simOnlyContent.benefits}
          CTA={simOnlyContent.heroCta}
          CTALink={simOnlyContent.CTALink}
          imageUrl={HeroImage}
          imageAlt={simOnlyContent.heroImageAlt}
        />
      </SiteContainer>
    </FullWidthContainer>

    <SiteContainer>
      <LogoGarden
        css={css`
          margin-left: 0;
          margin-right: 0;
        `}
      />
    </SiteContainer>

    <SiteContainer
      data-module-id={plans.length > 0 ? 'plan-modules-sim-only' : null}
      data-module_name="simple-plans"
      data-module_placement="products-section"
    >
      <PlanSection plans={plans} />
    </SiteContainer>

    <BlockContainer
      style={{
        backgroundColor: colors['UI/DarkGrey'],
      }}
    >
      <FiftyFifty imageTextSmall inverse>
        <img src={simOnlyContent.heroImgSrc} alt="" />
        {simOnlyContent.heroContent}
      </FiftyFifty>
    </BlockContainer>

    <SiteContainer>
      {simOnlyContent.fiftyFifties.map((fiftyFifty, index) => (
        <Fragment>
          <FiftyFifty
            index={index}
            imageTextSmall
            inverse={Math.abs(index % 2) === 1}
          >
            <img src={fiftyFifty.imageSrc} alt="" />
            {fiftyFifty.content}
          </FiftyFifty>
        </Fragment>
      ))}
    </SiteContainer>

    <SiteContainer>
      <CenteredButton>
        <Button
          href={simOnlyContent.CTALink}
          color={theme.colors.button.buttonColor}
        >
          {simOnlyContent.heroCta}
        </Button>
      </CenteredButton>
    </SiteContainer>
    <Spacer />

    <FullWidthContainer background={colors['UI/DarkGrey']}>
      <SiteContainer>
        <TrustPilot
          dataTags="SelectedReview"
          dataTestId="homepage-trustpilot-widget"
        />
      </SiteContainer>
    </FullWidthContainer>

    <SiteContainer>
      <Spacer />
      <FaqSection faqAccordions={simOnlyContent.faqAccordions} />
      <Spacer />
    </SiteContainer>
  </Fragment>
)

const simOnlyContent = {
  heading: 'Get the best SIM-only deals with SMARTY',
  subHeading: 'Enjoy the flexibility of our commitment-free SIM plans.',
  benefits: [
    'No lengthy contract',
    'No credit checks',
    'Unlimited calls & texts',
  ],
  heroContent: (
    <Fragment>
      <h2 className="kollektif-extra-large">
        The freedom of pay as you go. The benefits of a contract.{' '}
      </h2>
      <p className="margin--regular">
        Because your SIM is paid for in advance, you can enjoy your data
        allowance without worrying about an unexpected bill - just like regular
        SIM contracts.
      </p>
      <p className="margin--regular">
        But you also have the same peace of mind you get with a{' '}
        <TextLink to="/pay-as-you-go">pay as you go SIM</TextLink>: there’s{' '}
        <TextLink to="/no-credit-check-sim">no credit check</TextLink>, no
        minimum-term contract, the freedom to cancel at any time, and the
        flexibility to add more data whenever you need it.
      </p>
    </Fragment>
  ),
  heroImgSrc: PayAsYouGo,
  heroImageAlt: 'SIM only deals',
  heroCta: 'View our plans',
  CTALink: '#plans',
  seo: {
    title: 'SIM only deals',
    description:
      'SMARTY is a new SIM-only mobile network that’s built to be fair, transparent and smart.',
    keyboards: [
      'SIM only deals',
      'SIM only contracts',
      'SIM only plans',
      'SIM contracts',
    ],
  },
  faqAccordions: [
    {
      faqId: 'choose-sim-only-deal',
      faqTitle: 'Why choose a SIM-only deal?',
      faqTitleTestId: 'choose-sim-only-deal-title',
      faqContent:
        'If you already have a smartphone or tablet, then a SIM-only plan offers great value data and call swithout the hefty price tag of a normal phone contract – because it doesn’t include the price of the phone. Our SMARTY SIMs give you loads of choice from 1GB to Unlimited data, with unlimited calls and texts as standard on every plan. Plus, you can amend your plan as and when you need to.',
      faqContentTestId: 'choose-sim-only-deal-content',
    },
    {
      faqId: 'how-much-data-sim-only',
      faqTitle: 'How much data should I look for in a SIM-only deal?',
      faqTitleTestId: 'show-much-data-sim-only-title',
      faqContent:
        'It all depends on the type of user you are. A 2019 study by OFCOM showed that the average person used 2.9GB of mobile data per month. Our Data Discount plans give you the choice of 1GB, 2GB or 3GB of data, with the added bonus of money back for any data you don’t use in the month. But if you love to stream, share and play on your device, then maybe think bigger – get 30GB, 50GB or Unlimited data for a great price and never worry again.',
      faqContentTestId: 'how-much-data-sim-only-content',
    },
    {
      faqId: 'sim-card-do-i-need',
      faqTitle: 'What SIM card do I need?',
      faqTitleTestId: 'sim-card-do-i-need-title',
      faqContent:
        'There are three different types of SIM card that you may come across. A standard SIM, a smaller Micro SIM, and a tiny Nano SIM. The type of SIM card you’ll need depends on the phone or tablet  you have. Our SMARTY SIMs come with all three sizes combined in one plastic card – you just have to pop out the one that you need.',
      faqContentTestId: 'sim-card-do-i-need-content',
    },
    {
      faqId: 'easy-to-switch-network',
      faqTitle: 'How easy is it to switch networks?',
      faqTitleTestId: 'easy-to-switch-network-title',
      faqContent:
        'Super easy. Just contact your current provider and tell them you want to leave – you’ll need to be out of contract though, otherwise you may have to pay up the rest of your contract before you  switch. Once that’s done you can choose your new deal and purchase a SMARTY SIM. This will be sent to you in the post. You can keep your existing number too – request a PAC code from your old provider and transfer it over online on your SMARTY dashboard.',
      faqContentTestId: 'easy-to-switch-network-content',
    },
  ],
  fiftyFifties: [
    {
      imageSrc: DataSimDevice,
      content: (
        <Fragment>
          <h2 className="kollektif-extra-large">
            Also works with tablets, dongles and more{' '}
          </h2>
          <p className="margin--none">
            Our SIM-only plans also work with{' '}
            <TextLink to="/data-sim">tablets and mobile broadband</TextLink>{' '}
            devices like dongles and WiFi routers. Simply choose your plan, pop
            your SMARTY SIM in, and you’re good to go.
          </p>
        </Fragment>
      ),
    },
    {
      imageSrc: DataImage,
      content: (
        <Fragment>
          <h2 className="kollektif-extra-large">
            Data add-ons that make sense.
          </h2>
          <p className="margin--regular">
            If you run out of data during the month, an add-on will cost just £1
            per GB. This is the same amount you pay for data in your usual plan,
            so you’re not getting stung for buying additional data when you
            really need it.{' '}
          </p>
          <p className="margin--regular">
            Our add-ons also never expire - as long as you have a live
            pay-monthly SIM, your data is there for you to use. Nice.
          </p>
        </Fragment>
      ),
    },
    {
      imageSrc: UnlimitedImage,
      content: (
        <Fragment>
          <h2 className="kollektif-extra-large">
            Unlimited UK standard calls and texts. Always.
          </h2>
          <p className="margin--regular">
            Call your friends and family as much as you want.{' '}
          </p>
          <p className="margin--regular">Comes with all plans. No catch.</p>
        </Fragment>
      ),
    },
  ],
}

SimOnly.propTypes = {
  plans: {
    plans: PropTypes.array.isRequired,
  },
}

export default withApp(SimOnly)
